import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["club", "continue", "list"]
  static values = {
    selectedClass: String,
    selectedId: String,
    selectedName: String
  }

  connect() {
    this.restoreSelection()
    this.updateState()
  }

  choose(event) {
    const button = event.currentTarget
    
    // Prevent selection if club has unpaid cart
    if (button.dataset.hasUnpaidCart === "true") {
      event.preventDefault()
      return
    }
    
    this.selectedIdValue = button.dataset.clubId
    this.selectedNameValue = button.dataset.clubName
    this.highlightSelection(button)
    this.updateState()
  }

  continue(event) {
    if (!this.hasSelectedIdValue) {
      event.preventDefault()
      return
    }

    // Check if selected club has an unpaid cart
    const selectedButton = this.clubTargets.find((button) => button.dataset.clubId === this.selectedIdValue)
    if (selectedButton && selectedButton.dataset.hasUnpaidCart === "true") {
      event.preventDefault()
      this.showUnpaidCartModal(selectedButton)
      return
    }

    this.dispatch("club-selected", {
      detail: {
        clubId: this.selectedIdValue,
        clubName: this.selectedNameValue
      }
    })
  }

  showUnpaidCartModal(button) {
    const clubName = button.dataset.clubName
    const cartId = button.dataset.cartId
    const clubId = button.dataset.clubId

    // Create and show modal
    const modal = document.createElement("div")
    modal.className = "fixed inset-0 z-[60] flex items-center justify-center"
    modal.innerHTML = `
      <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" data-action="click->memberships-selection#closeBlockModal"></div>
      <div class="relative z-10 w-full max-w-md mx-4">
        <div class="rounded-2xl bg-white p-6 shadow-xl">
          <div class="mb-4 flex items-center gap-3">
            <div class="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-6 w-6 text-amber-600">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-slate-900">Active Cart Exists</h3>
          </div>
          <p class="mb-6 text-sm text-slate-600">
            You already have an active cart for <strong class="text-slate-900">${clubName}</strong>. 
            Please complete the existing checkout or delete the cart before starting a new membership registration.
          </p>
          <div class="flex flex-col gap-3">
            <a href="/members/carts?club_id=${clubId}" 
               class="flex-1 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white text-center transition hover:bg-slate-800">
              Resume Checkout
            </a>
            <button
              type="button"
              data-action="click->memberships-selection#deleteCart"
              data-cart-id="${cartId}"
              data-club-id="${clubId}"
              class="flex-1 rounded-full border border-red-300 px-4 py-2 text-sm font-semibold text-red-700 transition hover:border-red-400 hover:bg-red-50">
              Delete Cart
            </button>
            <button
              type="button"
              data-action="click->memberships-selection#closeBlockModal"
              class="flex-1 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </div>
      </div>
    `
    
    this.blockModal = modal
    document.body.appendChild(modal)
  }

  closeBlockModal(event) {
    if (this.blockModal) {
      this.blockModal.remove()
      this.blockModal = null
    }
  }

  deleteCart(event) {
    const button = event.currentTarget
    const cartId = button.dataset.cartId
    const clubId = button.dataset.clubId

    if (!confirm("Are you sure you want to delete this cart?")) {
      return
    }

    // Send delete request
    fetch(`/members/cart?club_id=${clubId}`, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "text/vnd.turbo-stream.html"
      }
    }).then(response => {
      if (response.ok) {
        this.closeBlockModal()
        // Reload the page to refresh the cart list
        window.location.reload()
      } else {
        alert("Failed to delete cart. Please try again.")
      }
    }).catch(error => {
      console.error("Error deleting cart:", error)
      alert("An error occurred. Please try again.")
    })
  }

  highlightSelection(selectedButton) {
    if (!this.hasClubTarget) return

    const classes = this.selectedClasses()
    this.clubTargets.forEach((button) => {
      button.classList.remove(...classes)
    })

    selectedButton.classList.add(...classes)
  }

  updateState() {
    if (this.hasContinueTarget) {
      this.continueTarget.disabled = !this.hasSelectedIdValue
    }
  }

  selectedClasses() {
    return (this.selectedClassValue || "border-slate-900 ring-2 ring-slate-200 shadow-md").split(/\s+/).filter(Boolean)
  }

  restoreSelection() {
    if (!this.hasSelectedIdValue || !this.hasClubTarget) return
    const selectedButton = this.clubTargets.find((button) => button.dataset.clubId === this.selectedIdValue)
    if (!selectedButton) return
    if (!this.hasSelectedNameValue || !this.selectedNameValue) {
      this.selectedNameValue = selectedButton.dataset.clubName
    }
    this.highlightSelection(selectedButton)
  }
}
